// Extracted from plans.ts to break a circular dependency:
// plans → business → financial → plans (PlanType)

export enum PlanType {
  HEALTH = "health",
  MEDICAL = "medical", // Treated as equivalent to HEALTH - normalized to HEALTH in most contexts
  DENTAL = "dental",
  VISION = "vision",
  LIFE = "life",
  VOLUNTARY_LIFE = "voluntary_life",
  ADD = "add",
  STD = "std",
  LTD = "ltd",
  ACCIDENT = "accident",
  CRITICAL_ILLNESS = "critical_illness",
  CANCER = "cancer",
  HOSPITAL_INDEMNITY = "hospital_indemnity",
  TELEHEALTH = "telehealth",
  LEGAL = "legal",
  ID_THEFT = "id_theft",
  FSA_HEALTH = "fsa_health",
  FSA_DEPENDENT_CARE = "fsa_dependent_care",
  PET = "pet",
  TRANSIT = "transit",
  CHIROPRACTIC = "chiropractic",
  ACUPUNCTURE = "acupuncture",
  EAP = "eap",
}

/**
 * Normalizes plan types to treat "medical" and "health" as equivalent.
 * Word & Brown API returns "Medical" but we standardize to "health" internally.
 * @param planType - The plan type to normalize
 * @returns The normalized plan type (medical -> health)
 */
export function normalizePlanType(planType: string | undefined | null): string {
  if (!planType) return "health";
  const normalized = planType.toLowerCase().trim();
  // Treat "medical" as "health"
  return normalized === "medical" ? "health" : normalized;
}

export function isLifeDisabilityType(planType: string | undefined | null): boolean {
  if (!planType) return false;
  const normalized = planType.toLowerCase().trim();
  return ["life", "add", "voluntary_life", "disability", "std", "ltd"].includes(normalized);
}

/**
 * Plan types that designate a beneficiary on enrollment (the payout recipient).
 * Life, voluntary life and AD&D all pay a death benefit, so all collect a beneficiary.
 */
export function requiresBeneficiary(planType: string | undefined | null): boolean {
  const normalized = (planType || "").toLowerCase().trim();
  return ["life", "voluntary_life", "add"].includes(normalized);
}

/**
 * Volume-rated plan types: rates are tables (per-$1k of coverage, per-age-band)
 * rather than flat amounts per coverage tier. Each row in a source CSV like
 * Ease's Enrollment Census is one enrollee's specific premium derived from
 * their coverage amount + age + salary — useless as a plan-level rate.
 *
 * Used by the migration wizard to skip auto-import of `planRates` and
 * `planContributions` for these types; broker configures the rate table
 * manually post-import.
 */
export function isVolumeRatedPlanType(
  planType: string | undefined | null,
): boolean {
  if (!planType) return false;
  const normalized = planType.toLowerCase().trim();
  return [
    "life",
    "add",
    "life_basic",
    "life_voluntary",
    "voluntary_life",
    "accidental_death",
    "add_basic",
    "add_voluntary",
    "disability",
    "std",
    "ltd",
    "accident",
    "critical_illness",
    "cancer",
    "hospital_indemnity",
  ].includes(normalized);
}

/**
 * FSA-family plan types: Health FSA, Dependent Care FSA, and
 * Commuter/Transit. These don't have plan-level rates — the employer
 * pays a flat admin fee outside this system and each employee elects a
 * dollar amount capped by IRS limits (annual for Health / Dependent
 * Care, monthly for Transit). The migration wizard's rate-model UX
 * (library match, age × tier matrix, tier rates, per-$1k volume) is
 * irrelevant for these plans; per-employee elections flow through
 * `enrollments.coverageAmount` instead.
 */
export function isFsaPlanType(
  planType: string | undefined | null,
): boolean {
  if (!planType) return false;
  const normalized = planType.toLowerCase().trim();
  return ["fsa_health", "fsa_dependent_care", "transit"].includes(normalized);
}

/**
 * UI ordering for plan types. Drives:
 *  - the type-sectioned employee Benefits tab (broker + employer portals)
 *  - the BenefitsConfigurationForm wizard step order
 *  - any future per-type report/summary
 *
 * Use canonical lowercase ids. `normalizePlanType("health")` -> "health",
 * but the section taxonomy treats medical/health as the same section
 * (see SECTION_ORDER below — "medical" section accepts both ids).
 */
export const TYPE_RANK = [
  "medical",
  "dental",
  "vision",
  "life",
  "voluntary_life",
  "add",
  "std",
  "ltd",
  "fsa_health",
  "fsa_dependent_care",
  "accident",
  "critical_illness",
  "cancer",
  "hospital_indemnity",
  "telehealth",
  "legal",
  "id_theft",
  "pet",
  "transit",
  "chiropractic",
  "acupuncture",
  "eap",
] as const;

export type PlanTypeId = (typeof TYPE_RANK)[number];

export interface BenefitSectionDef {
  id: string;
  title: string;
  /** Plan-type ids that belong in this section. */
  types: readonly string[];
  /** When true, the section header is collapsible (defaults open if any row exists). */
  collapsible?: boolean;
}

/**
 * Section grouping for the per-employee Benefits tab. Disability folds STD+LTD
 * into one header; FSAs share a "Spending accounts" header; everything else
 * voluntary collapses into a single section.
 */
export const SECTION_ORDER: readonly BenefitSectionDef[] = [
  { id: "medical",    title: "Medical",            types: ["medical", "health"] },
  { id: "dental",     title: "Dental",             types: ["dental"] },
  { id: "vision",     title: "Vision",             types: ["vision"] },
  { id: "life",       title: "Life / AD&D",        types: ["life", "voluntary_life", "add"] },
  { id: "disability", title: "Disability",         types: ["std", "ltd"] },
  { id: "fsa",        title: "Spending accounts",  types: ["fsa_health", "fsa_dependent_care"] },
  {
    id: "voluntary",
    title: "Voluntary benefits",
    types: ["accident", "critical_illness", "cancer", "hospital_indemnity", "telehealth", "legal", "id_theft", "pet", "transit", "chiropractic", "acupuncture", "eap"],
    collapsible: true,
  },
] as const;

/**
 * Per-type display metadata. `iconKey` is a stable id the UI maps to a
 * lucide-react icon (see client/src/components/benefits/v2/typeIcons.tsx).
 *
 * `accentBg` and `accentText` are tailwind utility classes (kept as full class
 * strings so Tailwind's content scanner picks them up). They're applied to the
 * row's icon tile and the type chip so each plan type carries a distinct color
 * identity across the page.
 */
export interface PlanTypeMeta {
  label: string;
  short: string;
  iconKey: string;
  accentBg: string;
  accentText: string;
}

export const TYPE_META: Record<string, PlanTypeMeta> = {
  medical:            { label: "Medical",                short: "Medical",  iconKey: "medical",            accentBg: "bg-sky-100 dark:bg-sky-950/40",       accentText: "text-sky-700 dark:text-sky-300" },
  health:             { label: "Medical",                short: "Medical",  iconKey: "medical",            accentBg: "bg-sky-100 dark:bg-sky-950/40",       accentText: "text-sky-700 dark:text-sky-300" },
  dental:             { label: "Dental",                 short: "Dental",   iconKey: "dental",             accentBg: "bg-cyan-100 dark:bg-cyan-950/40",     accentText: "text-cyan-700 dark:text-cyan-300" },
  vision:             { label: "Vision",                 short: "Vision",   iconKey: "vision",             accentBg: "bg-violet-100 dark:bg-violet-950/40", accentText: "text-violet-700 dark:text-violet-300" },
  life:               { label: "Life",                   short: "Life",     iconKey: "life",               accentBg: "bg-slate-100 dark:bg-slate-800",      accentText: "text-slate-700 dark:text-slate-300" },
  voluntary_life:     { label: "Voluntary Life",         short: "Vol Life", iconKey: "life",               accentBg: "bg-slate-100 dark:bg-slate-800",      accentText: "text-slate-700 dark:text-slate-300" },
  add:                { label: "AD&D",                   short: "AD&D",     iconKey: "add",                accentBg: "bg-slate-100 dark:bg-slate-800",      accentText: "text-slate-700 dark:text-slate-300" },
  std:                { label: "Short-term Disability",  short: "STD",      iconKey: "std",                accentBg: "bg-amber-100 dark:bg-amber-950/40",   accentText: "text-amber-800 dark:text-amber-300" },
  ltd:                { label: "Long-term Disability",   short: "LTD",      iconKey: "ltd",                accentBg: "bg-amber-100 dark:bg-amber-950/40",   accentText: "text-amber-800 dark:text-amber-300" },
  fsa_health:         { label: "Health Care FSA",        short: "FSA",      iconKey: "fsa_health",         accentBg: "bg-emerald-100 dark:bg-emerald-950/40", accentText: "text-emerald-700 dark:text-emerald-300" },
  fsa_dependent_care: { label: "Dependent Care FSA",     short: "DCFSA",    iconKey: "fsa_dependent_care", accentBg: "bg-emerald-100 dark:bg-emerald-950/40", accentText: "text-emerald-700 dark:text-emerald-300" },
  accident:           { label: "Accident",               short: "Accident", iconKey: "accident",           accentBg: "bg-orange-100 dark:bg-orange-950/40", accentText: "text-orange-700 dark:text-orange-300" },
  critical_illness:   { label: "Critical Illness",       short: "CI",       iconKey: "critical_illness",   accentBg: "bg-orange-100 dark:bg-orange-950/40", accentText: "text-orange-700 dark:text-orange-300" },
  cancer:             { label: "Cancer",                 short: "Cancer",   iconKey: "cancer",             accentBg: "bg-orange-100 dark:bg-orange-950/40", accentText: "text-orange-700 dark:text-orange-300" },
  hospital_indemnity: { label: "Hospital Indemnity",     short: "HI",       iconKey: "hospital_indemnity", accentBg: "bg-orange-100 dark:bg-orange-950/40", accentText: "text-orange-700 dark:text-orange-300" },
  telehealth:         { label: "Telehealth",             short: "Tele",     iconKey: "telehealth",         accentBg: "bg-orange-100 dark:bg-orange-950/40", accentText: "text-orange-700 dark:text-orange-300" },
  legal:              { label: "Legal",                  short: "Legal",    iconKey: "legal",              accentBg: "bg-orange-100 dark:bg-orange-950/40", accentText: "text-orange-700 dark:text-orange-300" },
  id_theft:           { label: "ID Theft",               short: "IDT",      iconKey: "id_theft",           accentBg: "bg-orange-100 dark:bg-orange-950/40", accentText: "text-orange-700 dark:text-orange-300" },
  pet:                { label: "Pet",                    short: "Pet",      iconKey: "pet",                accentBg: "bg-orange-100 dark:bg-orange-950/40", accentText: "text-orange-700 dark:text-orange-300" },
  transit:            { label: "Transit",                short: "Transit",  iconKey: "transit",            accentBg: "bg-orange-100 dark:bg-orange-950/40", accentText: "text-orange-700 dark:text-orange-300" },
  chiropractic:       { label: "Chiropractic",           short: "Chiro",    iconKey: "chiropractic",       accentBg: "bg-orange-100 dark:bg-orange-950/40", accentText: "text-orange-700 dark:text-orange-300" },
  acupuncture:        { label: "Acupuncture",            short: "Acu",      iconKey: "acupuncture",        accentBg: "bg-orange-100 dark:bg-orange-950/40", accentText: "text-orange-700 dark:text-orange-300" },
  eap:                { label: "EAP",                    short: "EAP",      iconKey: "eap",                accentBg: "bg-orange-100 dark:bg-orange-950/40", accentText: "text-orange-700 dark:text-orange-300" },
};

const FALLBACK_META: PlanTypeMeta = {
  label: "Other",
  short: "Other",
  iconKey: "other",
  accentBg: "bg-stone-100 dark:bg-stone-800",
  accentText: "text-stone-700 dark:text-stone-300",
};

export function getPlanTypeMeta(planType: string | undefined | null): PlanTypeMeta {
  const normalized = (planType || "").toLowerCase().trim();
  if (TYPE_META[normalized]) return TYPE_META[normalized];
  return {
    ...FALLBACK_META,
    label: normalized ? normalized.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Other",
  };
}

export function getPlanTypeAccent(planType: string | undefined | null): { accentBg: string; accentText: string } {
  const meta = getPlanTypeMeta(planType);
  return { accentBg: meta.accentBg, accentText: meta.accentText };
}

/**
 * Returns the section that owns this plan type, or the "voluntary" section
 * as a fallback for anything not explicitly listed.
 */
export function getSectionForType(planType: string | undefined | null): BenefitSectionDef {
  const normalized = (planType || "").toLowerCase().trim();
  return (
    SECTION_ORDER.find((s) => s.types.includes(normalized)) ??
    SECTION_ORDER[SECTION_ORDER.length - 1]
  );
}

/** Numeric rank for sorting plan types in the canonical UI order. Unknown types sort last. */
export function planTypeRank(planType: string | undefined | null): number {
  const normalized = (planType || "").toLowerCase().trim();
  const idx = (TYPE_RANK as readonly string[]).indexOf(normalized === "health" ? "medical" : normalized);
  return idx === -1 ? TYPE_RANK.length : idx;
}

/**
 * Whether at most ONE active/pending enrollment is allowed per employee for
 * this plan type. Used to gate the Configure Benefits wizard so brokers can't
 * accidentally double-enroll an employee into two medical plans, etc.
 *
 * Rule: everything except the Voluntary section (Accident, Critical Illness,
 * Cancer, Hospital Indemnity, Telehealth, Legal, ID Theft, Pet, Transit) is
 * exclusive. The voluntary cluster is additive — an employee can elect any
 * combination of those.
 *
 * Exception: Chiropractic, Acupuncture, and EAP render under the Voluntary
 * header for layout but are one-per-employee lines (you don't stack two
 * chiropractic plans), so they're forced exclusive via EXCLUSIVE_VOLUNTARY_TYPES.
 *
 * Unknown types default to non-exclusive (additive), matching the voluntary
 * fallback in `getSectionForType()`.
 */
const EXCLUSIVE_VOLUNTARY_TYPES = new Set(["chiropractic", "acupuncture", "eap"]);

export function isExclusivePlanType(planType: string | undefined | null): boolean {
  const normalized = (planType || "").toLowerCase().trim();
  if (EXCLUSIVE_VOLUNTARY_TYPES.has(normalized)) return true;
  const section = getSectionForType(planType);
  return !section.collapsible;
}
